/**
 * Voice-message helpers: record with MediaRecorder, upload via the SDK media
 * API, and send a spec-compliant m.audio voice message event.
 */
import { EventType, MsgType, type MatrixClient } from "matrix-js-sdk";
import { generateWaveform } from "../lib/waveform";

const PREFERRED_MIME = "audio/webm;codecs=opus";

export interface RecordingResult {
  blob: Blob;
  mimetype: string;
  durationMs: number;
  waveform: number[];
}

/** Whether the browser can record audio at all. */
export function isRecordingSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined"
  );
}

function pickMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "audio/webm";
  if (MediaRecorder.isTypeSupported(PREFERRED_MIME)) return PREFERRED_MIME;
  if (MediaRecorder.isTypeSupported("audio/webm")) return "audio/webm";
  if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) return "audio/ogg;codecs=opus";
  return "audio/webm";
}

/**
 * A small controller around MediaRecorder that also samples amplitude via an
 * AnalyserNode so we can produce a waveform. Call `start()`, then `stop()`
 * which resolves with the recorded blob + metadata.
 */
export interface VoiceRecorderOptions {
  /** Called ~every 60ms while recording with the current level (0..1). */
  onLevel?: (level01: number) => void;
}

export class VoiceRecorder {
  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private chunks: Blob[] = [];
  private amplitudes: number[] = [];
  private startedAt = 0;
  private sampleTimer: ReturnType<typeof setInterval> | null = null;
  private mimetype = "audio/webm";
  private onLevel: ((level01: number) => void) | null;

  constructor(options: VoiceRecorderOptions = {}) {
    this.onLevel = options.onLevel ?? null;
  }

  async start(): Promise<void> {
    if (!isRecordingSupported()) {
      throw new Error("Voice recording is not supported in this browser.");
    }
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.mimetype = pickMimeType();
    this.recorder = new MediaRecorder(this.stream, { mimeType: this.mimetype });
    this.chunks = [];
    this.amplitudes = [];

    this.recorder.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };

    // Amplitude sampling for the waveform.
    try {
      const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioCtx = new Ctx();
      const source = this.audioCtx.createMediaStreamSource(this.stream);
      this.analyser = this.audioCtx.createAnalyser();
      this.analyser.fftSize = 256;
      source.connect(this.analyser);
      const buffer = new Uint8Array(this.analyser.frequencyBinCount);
      this.sampleTimer = setInterval(() => {
        if (!this.analyser) return;
        this.analyser.getByteTimeDomainData(buffer);
        let peak = 0;
        for (const v of buffer) peak = Math.max(peak, Math.abs(v - 128));
        this.amplitudes.push(peak);
        this.onLevel?.(Math.min(1, peak / 128));
      }, 60);
    } catch {
      // Waveform sampling is best-effort; recording still works without it.
    }

    this.startedAt = Date.now();
    this.recorder.start();
  }

  /** Stop recording and resolve with the blob + metadata. */
  stop(): Promise<RecordingResult> {
    return new Promise<RecordingResult>((resolve, reject) => {
      const recorder = this.recorder;
      if (!recorder) {
        reject(new Error("Recorder is not active."));
        return;
      }
      recorder.onstop = () => {
        const durationMs = Date.now() - this.startedAt;
        const blob = new Blob(this.chunks, { type: this.mimetype });
        const waveform = generateWaveform(this.amplitudes, 40);
        this.cleanup();
        resolve({ blob, mimetype: this.mimetype, durationMs, waveform });
      };
      try {
        recorder.stop();
      } catch (err) {
        this.cleanup();
        reject(err instanceof Error ? err : new Error("Failed to stop recording."));
      }
    });
  }

  /** Abort recording without producing a result. */
  cancel(): void {
    try {
      this.recorder?.stop();
    } catch {
      // ignore
    }
    this.cleanup();
  }

  private cleanup(): void {
    if (this.sampleTimer) clearInterval(this.sampleTimer);
    this.sampleTimer = null;
    // No further level callbacks once recording ends.
    this.onLevel = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    void this.audioCtx?.close().catch(() => undefined);
    this.audioCtx = null;
    this.analyser = null;
    this.recorder = null;
  }
}

/**
 * Build the content for a spec-compliant voice message event. Pure function,
 * unit-testable independently of the SDK.
 */
export function buildVoiceMessageContent(params: {
  mxcUrl: string;
  durationMs: number;
  mimetype: string;
  size: number;
  waveform: number[];
}): Record<string, unknown> {
  return {
    msgtype: MsgType.Audio,
    body: "Voice message",
    url: params.mxcUrl,
    info: {
      duration: params.durationMs,
      mimetype: params.mimetype,
      size: params.size,
    },
    "org.matrix.msc1767.audio": {
      duration: params.durationMs,
      waveform: params.waveform,
    },
    "org.matrix.msc3245.voice": {},
  };
}

/** Upload the recording and send the voice-message event to a room. */
export async function sendVoiceMessage(
  client: MatrixClient,
  roomId: string,
  recording: RecordingResult,
): Promise<void> {
  const upload = await client.uploadContent(recording.blob, {
    type: recording.mimetype,
    name: "voice-message.webm",
  });
  const mxcUrl = upload.content_uri;

  const content = buildVoiceMessageContent({
    mxcUrl,
    durationMs: recording.durationMs,
    mimetype: recording.mimetype,
    size: recording.blob.size,
    waveform: recording.waveform,
  });

  // Content includes MSC voice-message fields not present in the SDK's typed
  // RoomMessage content, so we widen to satisfy the typed overload.
  await client.sendEvent(roomId, EventType.RoomMessage, content as never);
}
